import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import ProjectsList from "./pages/ProjectsList";
import AdminTenants from "./pages/AdminTenants";
import ProjectLayout from "./layout/ProjectLayout";
import Dashboard from "./pages/project/Dashboard";
import Charter from "./pages/project/Charter";
import SchedulePage from "./pages/project/SchedulePage";
import ChangeManagement from "./pages/project/ChangeManagement";
import ArtifactPage from "./pages/project/ArtifactPage";
import SingletonPage from "./pages/project/SingletonPage";
import FmeaPage from "./pages/project/FmeaPage";
import StatusReports from "./pages/project/StatusReports";
import ToolsPage from "./pages/project/ToolsPage";
import MeetingsPage from "./pages/project/MeetingsPage";
import RequirementsPage from "./pages/project/RequirementsPage";
import TeamPage from "./pages/TeamPage";
import { MODULES } from "./config/modules";
import MembersPage from "./pages/project/MembersPage";
import GaugeRRPage from "./pages/project/GaugeRRPage";
import CapabilityPage from "./pages/project/CapabilityPage";
import ProcessMapPage from "./pages/project/ProcessMapPage";
import WorkflowsPage from "./pages/project/WorkflowsPage";
import KanbanPage from "./pages/project/KanbanPage";
import WorkloadPage from "./pages/project/WorkloadPage";
import TeamStatusPage from "./pages/project/TeamStatusPage";
import ActionsDecisionsPage from "./pages/project/ActionsDecisionsPage";
import RiskIssuePage from "./pages/project/RiskIssuePage";
import HuddleBoardPage from "./pages/project/HuddleBoardPage";
import LessonsHub from "./pages/LessonsHub";
import TemplatesPage from "./pages/TemplatesPage";
import CapacityPlanPage from "./pages/project/CapacityPlanPage";
import TimesheetPage from "./pages/project/TimesheetPage";
import DependencyMapPage from "./pages/project/DependencyMapPage";
import ScenariosPage from "./pages/project/ScenariosPage";
import FinancePage from "./pages/project/FinancePage";
import { BillingPage, PaymentSuccess, InviteAccept } from "./pages/BillingAndInvite";
import PortfolioPage from "./pages/PortfolioPage";
import HomePage from "./pages/HomePage";
import SignupPage from "./pages/SignupPage";
import PricingPage from "./pages/PricingPage";

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "platform_admin") return <Navigate to="/projects" replace />;
  return children;
}

const ARTIFACT_ROUTES = Object.keys(MODULES).filter((t) => !["meetings", "requirements", "action_decision_log", "risk_issue_log", "finance_lines"].includes(t));

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/portfolio" element={<Protected><PortfolioPage /></Protected>} />
            <Route path="/lessons" element={<Protected><LessonsHub /></Protected>} />
            <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
            <Route path="/projects" element={<Protected><ProjectsList /></Protected>} />
            <Route path="/team" element={<Protected><TeamPage /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><AdminTenants /></Protected>} />
            <Route path="/projects/:projectId" element={<Protected><ProjectLayout /></Protected>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="charter" element={<Charter />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="kanban" element={<KanbanPage />} />
              <Route path="workload" element={<WorkloadPage />} />
              <Route path="team-status" element={<TeamStatusPage />} />
              <Route path="action_decision_log" element={<ActionsDecisionsPage />} />
              <Route path="risk_issue_log" element={<RiskIssuePage />} />
              <Route path="huddle" element={<HuddleBoardPage />} />
              <Route path="capacity-plan" element={<CapacityPlanPage />} />
              <Route path="timesheet" element={<TimesheetPage />} />
              <Route path="dependency-map" element={<DependencyMapPage />} />
              <Route path="scenarios" element={<ScenariosPage />} />
              <Route path="finance_lines" element={<FinancePage />} />
              <Route path="changes" element={<ChangeManagement />} />
              <Route path="status-reports" element={<StatusReports />} />
              <Route path="signoff" element={<SingletonPage kind="signoff" />} />
              <Route path="closeout" element={<SingletonPage kind="closeout" />} />
              <Route path="fmea" element={<FmeaPage />} />
              <Route path="meetings" element={<MeetingsPage />} />
              <Route path="requirements" element={<RequirementsPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="gauge-rr" element={<GaugeRRPage />} />
              <Route path="capability" element={<CapabilityPage />} />
              <Route path="process-map" element={<ProcessMapPage />} />
              <Route path="workflows-page" element={<WorkflowsPage />} />
              <Route path="tools" element={<ToolsPage />} />
              {ARTIFACT_ROUTES.map((t) => (
                <Route key={t} path={t} element={<ArtifactPage type={t} />} />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
